import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = {
    eventId: Number,
    channel: String
  }

  static targets = [
    "progressBar",
    "progressPercentage",
    "currentStep",
    "timeRemaining",
    "step1", "step2", "step3",
    "errorState",
    "errorMessage"
  ]

  connect() {
    console.log("Event processing controller connected", {
      eventId: this.eventIdValue,
      channel: this.channelValue
    })

    this.completed = false

    // Subscribe to the channel
    this.subscription = consumer.subscriptions.create(
      {
        channel: "EventProcessingChannel",
        event_id: this.eventIdValue
      },
      {
        connected: () => {
          console.log("Connected to EventProcessingChannel")
        },
        disconnected: () => {
          console.log("Disconnected from EventProcessingChannel")
        },
        received: (data) => {
          console.log("Received data:", data)
          this.handleUpdate(data)
        }
      }
    )

    // Start countdown timer
    this.startTimer()

    // Start polling fallback in case WebSocket misses the broadcast
    this.startPolling()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  }

  handleUpdate(data) {
    if (this.completed) return

    switch(data.status) {
      case 'processing':
        this.updateProgress(40, "Analyzing your event requirements...")
        this.updateStep(2, 'active')
        break

      case 'completed':
        this.completed = true
        this.updateProgress(100, "Event plan ready!")
        this.updateStep(2, 'completed')
        this.updateStep(3, 'completed')

        // Redirect after a brief delay
        setTimeout(() => {
          if (data.redirect_url) {
            window.location.href = data.redirect_url
          }
        }, 1000)
        break

      case 'failed':
        this.completed = true
        this.showError(data.error || "An error occurred during processing")
        break
    }
  }

  updateProgress(percentage, message) {
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressPercentageTarget.textContent = `${percentage}%`

    if (message) {
      this.currentStepTarget.textContent = message
    }
  }

  updateStep(stepNumber, status) {
    const stepTarget = this[`step${stepNumber}Target`]
    const iconDiv = stepTarget.querySelector('div:first-child')
    const textSpan = stepTarget.querySelector('span')

    switch(status) {
      case 'active':
        iconDiv.className = "w-5 h-5 border-2 border-teal-500 rounded-full mr-3 relative"
        iconDiv.innerHTML = '<div class="absolute inset-0 flex items-center justify-center"><div class="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div></div>'
        textSpan.className = "text-navy-900 font-medium"
        break

      case 'completed':
        iconDiv.className = "w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-3"
        iconDiv.innerHTML = '<i class="ph-fill ph-check text-white text-xs"></i>'
        textSpan.className = "text-gray-700"
        break
    }
  }

  showError(message) {
    this.element.querySelector('.bg-white.rounded-xl').classList.add('hidden')
    this.errorStateTarget.classList.remove('hidden')
    this.errorMessageTarget.textContent = message
  }

  startTimer() {
    let seconds = 15

    this.timer = setInterval(() => {
      if (seconds > 0) {
        seconds--
        this.timeRemainingTarget.textContent = `~${seconds} seconds`
      } else {
        this.timeRemainingTarget.textContent = "Almost done..."
      }
    }, 1000)
  }

  startPolling() {
    // Poll every 3 seconds as a fallback if WebSocket misses the broadcast
    this.pollTimer = setInterval(() => {
      if (this.completed) {
        clearInterval(this.pollTimer)
        return
      }

      fetch(`/events/${this.eventIdValue}/processing.json`, {
        headers: { "Accept": "application/json" }
      })
        .then(response => response.json())
        .then(data => {
          if (this.completed) return

          if (data.status === 'completed') {
            this.handleUpdate({ status: 'completed', redirect_url: data.redirect_url })
          } else if (data.status === 'failed') {
            this.handleUpdate({ status: 'failed', error: "Processing failed. Please try again." })
          }
        })
        .catch(error => {
          console.warn("Polling check failed:", error)
        })
    }, 3000)
  }
}
